import React, { useRef, useState, useEffect } from 'react'
import { SkipLink, Layout, Main } from '@deque/cauldron-react'
import PageNav from '../components/ContentPage/PageNav'
import { useGatsbyPageContext } from '../context/GatsbyPageContext'
import { VersionSelectModal } from '../components/ContentPage/VersionSelectModal'
import ArticleHeader from '../components/ContentPage/ArticleHeader'
import Breadcrumb from '../components/ContentPage/Breadcrumb'
import useSiteMetadata from '../hooks/use-site-metadata'
import './content-page.scss'
import ModuleSearch from '../components/Search/ModuleSearch'
import { Helmet } from 'react-helmet'
import createStringJoiner from '../lib/join-truthy-strings'
import Footer from '../components/Footer'
import Header from '../components/Header'
import Logo from '../components/Header/Logo'
import { SendFeedbackModal } from '../components/ContentPage/SendFeedbackModal'
import { useFeedbackModal } from '../context/FeedbackModal'
import LanguageSelector from '../components/LanguageSelector'
import { navigate } from '@reach/router'
import createPagePath from '../lib/create-page-path'
import { useTranslation } from 'react-i18next'
import { setLanguage, useLanguage } from '../context/Language'
import { LanguageCode } from '../types/language'
import ThemeToggle from '../components/ThemeToggle'

interface ContentPageLayoutProps {
  children: React.ReactElement
}

export default function ContentPageLayout(props: ContentPageLayoutProps) {
  const { children } = props
  const page = useGatsbyPageContext()
  const {
    subtitle,
    productId,
    version,
    locale,
    latestVersion,
    pageId,
    isPublic,
  } = page
  const site = useSiteMetadata()
  const [showNav, setShowNav] = useState(false)
  const [showVersionModal, setShowVersionModal] = React.useState(false)
  const { state: showFeedbackModal, dispatch: dispatchFeedbackModal } =
    useFeedbackModal()
  const [collapseBreadcrumb, setCollapseBreadcrumb] = useState(false)
  const layoutRef = useRef<HTMLDivElement>(null)
  const mainRef = useRef<HTMLDivElement>(null)
  const showNavButtonRef = useRef<HTMLButtonElement>(null)
  const hideNavButtonRef = useRef<HTMLButtonElement>(null)
  const { t } = useTranslation()
  const { dispatch: dispatchLanguage } = useLanguage()

  const toggleNav = () => {
    const isShown = !showNav
    setShowNav(isShown)
    if (isShown) {
      hideNavButtonRef.current?.focus()
    } else {
      showNavButtonRef.current?.focus()
    }
  }

  const dismissNav = () => {
    setShowNav(false)
    const mainEl = mainRef.current
    if (!mainEl) {
      return
    }
    const onBlur = () => {
      mainEl.removeAttribute('tabindex')
      mainEl.removeEventListener('blur', onBlur)
    }
    mainEl.setAttribute('tabindex', '0')
    mainEl.addEventListener('blur', onBlur)
    mainEl.focus()
  }

  const pageNavSkiplinkLabel = `Skip to page navigation`

  // Collapse/expand breadcrumb
  useEffect(() => {
    const layoutEl = layoutRef.current as HTMLDivElement
    const update = () => {
      if (!collapseBreadcrumb && layoutEl.scrollTop > 0) {
        setCollapseBreadcrumb(true)
      } else if (collapseBreadcrumb && layoutEl.scrollTop === 0) {
        setCollapseBreadcrumb(false)
      }
    }
    update()
    layoutEl.addEventListener('scroll', update)
    return () => layoutEl.removeEventListener('scroll', update)
  }, [layoutRef, collapseBreadcrumb])

  // Setting id on the page-nav skiplink so it can be hidden
  // when the page nav is not permanent.
  // The Cauldron SkipLink component does not allow setting
  // classes or ids as props.
  useEffect(() => {
    const pageNavSkiplink = document.querySelector(
      `[aria-label="${pageNavSkiplinkLabel}"]`
    )
    if (pageNavSkiplink) {
      pageNavSkiplink.setAttribute('id', 'page-nav-skiplink')
    }
  }, [])

  // Scroll to the top of the main content area if the URL does not target a specific anchor.
  // Should run every time the page changes.
  useEffect(() => {
    if (location.hash.length <= 0) {
      mainRef.current?.scrollIntoView()
    }
  }, [page.title])

  // Scroll to the anchor specified by the URL hash.
  // Only needs to run on initial page load.
  useEffect(() => {
    const anchorId = location.hash.length && location.hash.replace(/^#/, '')
    if (!anchorId) {
      return
    }
    const anchorEl = document.getElementById(anchorId)
    if (!anchorEl) {
      return
    }
    anchorEl.scrollIntoView()
  }, [])

  useEffect(() => {
    setLanguage(dispatchLanguage, locale as LanguageCode)
  }, [locale])

  // Generate a meta description for the page
  const description = createStringJoiner(
    page.subtitle,
    page.title,
    page.version
  )()

  // Generate a document title for the page
  const documentTitle = createStringJoiner(
    page.title,
    page.version,
    t(`contentPackage.${productId}.name`),
    site.title
  )()

  return (
    <>
      <Helmet>
        <title>{documentTitle}</title>
        <meta name="docsearch:product" content={productId} />
        <meta name="docsearch:version" content={version} />
        <meta name="docsearch:language" content={locale} />
        <meta
          name="docsearch:isLatestVersion"
          content={JSON.stringify(version === latestVersion)}
        />
        <meta name="description" content={description} />
        {isPublic && (
          <link
            rel="alternate"
            type="text/markdown"
            href={`${createPagePath({
              productId,
              version,
              locale,
              pageId,
            })}.md`}
          />
        )}
      </Helmet>

      <SkipLink
        target={'#page-navigation'}
        targetText="Page Navigation"
        aria-label={pageNavSkiplinkLabel}
      />
      <SkipLink
        target={'#main-content'}
        targetText="Main Content"
        aria-label="Skip to main content"
      />

      <VersionSelectModal
        show={showVersionModal}
        onClose={() => setShowVersionModal(false)}
      />

      <SendFeedbackModal
        show={showFeedbackModal}
        onClose={() => dispatchFeedbackModal({ type: 'update', value: false })}
      />

      <Header>
        <div className="cluster--left">
          <Logo hideSubtitle />
          <span className="Header__product-name">
            {t(`contentPackage.${productId}.name`)}
          </span>
        </div>
        <div className="cluster--right">
          <ModuleSearch
            productId={productId}
            version={version}
            language={locale}
          />
          <LanguageSelector
            onSelect={(languageCode) => {
              navigate(
                createPagePath({
                  productId,
                  version,
                  locale: languageCode,
                  pageId,
                })
              )
            }}
          />
          <ThemeToggle />
        </div>
      </Header>

      <PageNav
        show={showNav}
        dismissNav={dismissNav}
        hideNavButtonRef={
          hideNavButtonRef as React.RefObject<HTMLButtonElement>
        }
      />

      <Layout layoutRef={layoutRef}>
        <Main id="main-content" mainRef={mainRef}>
          <Breadcrumb
            collapsed={collapseBreadcrumb}
            toggleNav={toggleNav}
            showNavButtonRef={
              showNavButtonRef as React.RefObject<HTMLButtonElement>
            }
          />
          <article className="Article">
            <ArticleHeader
              mainRef={mainRef as React.RefObject<HTMLDivElement>}
              title={page.title}
              subtitle={subtitle}
              productVersion={version}
              showVersionModal={() => setShowVersionModal(true)}
            />
            {children}
          </article>
          <Footer />
        </Main>
      </Layout>
    </>
  )
}
